/**
 * Load external script file
 * @param {object} args
 *  @param {string} args.src - src path to load
 *  @param {string} [args.id] - script element id (optional - default : src filename)
 *  @param {string} [args.fallbackSrc] - alternative src path if the first fails (optional)
 *  @param {typeof keyof HTMLScriptElement} [args.props] - additional properties
 * @returns {HTMLScriptElement} the script element if succeed
 */
export const loadScript = (args) => {
    return new Promise((resolve, reject) => {
        let script = document.createElement('script');
        script.src = args.src;
        script.id = args.id || args.src.split('/').pop()?.split('.js')?.shift() || args.src;
        script.async = args.async || false;
        if (args.extras)
            script = { ...script, ...args.extras };
        const existing = document.getElementById(script.id);
        existing && document.head.removeChild(existing);
        document.head.appendChild(script);
        script.onload = () => resolve(script);
        script.onerror = () => args.fallbackSrc ? loadScript({ ...args, src: args.fallbackSrc, fallbackSrc: null })
            .then(fallbackScript => resolve(fallbackScript))
            .catch(() => reject()) : reject();
    });
};
/**
 * CONVERT camelCase String to anything{separtor}case
 *
 * @param {String} [str] - camelCase string
 * @param {String} [separator]
 * @returns {String} - converted string
 */
export const camelCaseTo = (str, separator) => str ? str.replace(/([a-zA-Z])(?=[A-Z])/g, `$1${separator}`).toLowerCase() : null;
/**
* CONVERT camelCase String to kebab-case
*
* @param {String} [str] - camelCase string
* @returns {String} - kebab-case string
*/
export const camelCaseToDash = (str) => camelCaseTo(str, '-');
/**
* CONVERT camelCase String to snake_case
*
* @param {String} [str] - camelCase string
* @returns {String} - snake_case string
*/
export const camelCaseToSnake = (str) => camelCaseTo(str, '_');
/**
 * Duplicate object
 * @param {Object} source
 * @returns {Object|any} - returns copied object.
 * If source isn't an object, returns source
 */
export const copyObject = (source) => 'object' === typeof source ? JSON.parse(JSON.stringify(source)) : source;
/**
 * Assign object's getters & setters from properties record
 * @param object
 * @param properties
 * @returns {object}
 */
export const setAccessors = (object, properties) => {
    return Object.defineProperties(object, Object.keys(properties).reduce((obj, key) => {
        obj[key] = {
            get() {
                return properties[key];
            },
            set(value) {
                properties[key] = value;
            }
        };
        return obj;
    }, {}));
};
/**
 * wait for a condition to be true
 * @param {boolean}condition
 * @param {number} maxTime
 * @returns {Promise}
 */
export const waitFor = (condition, maxTime) => {
    const response = () => 'function' === typeof condition ? condition() : condition;
    return new Promise((resolve, reject) => {
        let time = 0;
        if (response()) {
            resolve(response());
            return;
        }
        let interval = setInterval(() => {
            if (response()) {
                clearInterval(interval);
                resolve(response());
            }
            time = time + 500;
            if (time >= (maxTime || 3000)) {
                clearInterval(interval);
                reject(false);
            }
        }, 500);
    });
};
/**
 * @function objectToQueryString
 * convert Key value Object to query string
 * @param {Record} obj
 * @returns {string}
 */
export const objectToQueryString = (obj) => Object.entries(obj).map((([key, value]) => `${key}=${value}`)).join('&');
/**
 * @function capitalize
 * first letter uppercase
 * @param {string} str
 * @returns {string}
 */
export const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
/**
 * Test if a value is an Object
 * @param {unknown} arg
 * @returns {boolean}
 */
export const isObject = (arg) => 'object' === typeof arg && !Array.isArray(arg);
/**
 * Deep Object merging
 * @param {object} destination
 * @param {object|object[]} sources
 * @returns {object}
 */
export const mergeObject = (destination, sources) => {
    let output = Object.assign({}, destination);
    sources = Array.isArray(sources) ? sources : [sources];
    sources.map((source) => {
        if (isObject(destination) && isObject(source)) {
            for (const key in source) {
                if (isObject(source[key])) {
                    output[key] = mergeObject((isObject(output[key]) ? output[key] : {}), source[key]);
                }
                else {
                    output[key] = source[key];
                }
            }
        }
        ;
    });
    return output;
};
